// src/components/hospitalizacion/NuevoIngresoModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Autocomplete, Grid, Typography,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import { Save, Close } from '@mui/icons-material';
import api from '../../api/api';

const NuevoIngresoModal = ({ open, onClose, onSuccess }) => {
  // --- ESTADOS ---
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteOptions, setClienteOptions] = useState([]);
  const [pacientesCliente, setPacientesCliente] = useState([]);
  const [veterinarios, setVeterinarios] = useState([]);
  const [razas, setRazas] = useState([]);

  const [loadingPacientes, setLoadingPacientes] = useState(false);
  const [loadingVeterinarios, setLoadingVeterinarios] = useState(false);
  const [loadingGuardar, setLoadingGuardar] = useState(false);

  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  // Ahora guardamos el objeto completo del veterinario
  const [veterinarioSeleccionado, setVeterinarioSeleccionado] = useState(null);

  const [motivoIngreso, setMotivoIngreso] = useState('');
  const [diagnosticoPresuntivo, setDiagnosticoPresuntivo] = useState('');
  const [idJaula, setIdJaula] = useState('');
  const [error, setError] = useState('');

  // --- BÚSQUEDA DE CLIENTES ---
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (clienteSearch.trim().length >= 2) {
        try {
          const res = await api.get(`/clientes/buscar?terminos_busqueda=${encodeURIComponent(clienteSearch.trim())}`);
          setClienteOptions(res.data);
        } catch (err) {
          setClienteOptions([]);
        }
      } else {
        setClienteOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [clienteSearch]);

  // --- CARGA DE VETERINARIOS (Filtrado por Rol 2, 3, 4) ---
  useEffect(() => {
    if (!open) return;
    const cargarVeterinarios = async () => {
      setLoadingVeterinarios(true);
      try {
        const res = await api.get('/usuarios/');
        // Filtramos por habilitados y roles específicos (2: Admin, 3: Vet, 4: Recepción/Asistente)
        const rolesAutorizados = [2, 3, 4];
        const filtrados = res.data.filter(u => 
          u.habilitado === true && 
          rolesAutorizados.includes(u.id_rol)
        );
        setVeterinarios(filtrados);
      } catch (err) {
        console.error('Error cargando veterinarios:', err);
      } finally {
        setLoadingVeterinarios(false);
      }
    };
    cargarVeterinarios();
  }, [open]);

  // --- OTRAS CARGAS (Razas y Pacientes) ---
  useEffect(() => {
    api.get('/razas/').then(res => setRazas(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (clienteSeleccionado?.id_cliente) {
      setLoadingPacientes(true);
      api.get(`/pacientes/por-cliente/${clienteSeleccionado.id_cliente}`)
        .then(res => {
          // 1. Filtramos los pacientes que NO están fallecidos
          // Consideramos False o null/undefined como "no fallecido"
          const pacientesActivos = res.data.filter(p => !p.es_fallecido);

          // 2. Mapeamos solo los activos para agregar la info de la raza
          const pacientesConRaza = pacientesActivos.map(p => {
            const razaObj = razas.find(r => r.id_raza === p.id_raza);
            return {
              ...p,
              raza_nombre: razaObj?.nombre_raza || 'Sin raza',
              especie_nombre: razaObj?.especie?.nombre_especie || 'Sin especie'
            };
          });

          setPacientesCliente(pacientesConRaza);
        })
        .catch(err => {
          console.error("Error cargando pacientes:", err);
          setPacientesCliente([]);
        })
        .finally(() => setLoadingPacientes(false));
    } else {
      setPacientesCliente([]);
      setPacienteSeleccionado(null);
    }
  }, [clienteSeleccionado, razas]);

  // --- GUARDAR ---
  const handleGuardar = async () => {
    if (!clienteSeleccionado) return setError('Debe seleccionar un cliente');
    if (!pacienteSeleccionado) return setError('Debe seleccionar una mascota');
    if (!motivoIngreso.trim()) return setError('El motivo de ingreso es obligatorio');

    setLoadingGuardar(true);
    setError('');

    const payload = {
      id_paciente: pacienteSeleccionado.id_paciente,
      id_sucursal: clienteSeleccionado.id_sucursal,
      id_veterinario: veterinarioSeleccionado?.id_usuario || null,
      veterinario_responsable: veterinarioSeleccionado?.nombre_usuario || null,
      motivo_ingreso: motivoIngreso.trim(),
      diagnostico_presuntivo: diagnosticoPresuntivo.trim() || null,
      id_jaula: idJaula.trim() ? Number(idJaula.trim()) : null,
    };

    try {
      const res = await api.post('/hospitalizaciones/', payload);
      onSuccess?.(res.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar');
    } finally {
      setLoadingGuardar(false);
    }
  };

  const handleClose = () => {
    setClienteSeleccionado(null);
    setPacienteSeleccionado(null);
    setVeterinarioSeleccionado(null);
    setMotivoIngreso('');
    setDiagnosticoPresuntivo('');
    setIdJaula('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
        Nuevo Ingreso Hospitalario
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* AUTOCOMPLETE CLIENTE */}
          <Grid item xs={12}>
            <Autocomplete
              options={clienteOptions}
              getOptionLabel={(option) => option.nombre_cliente || ''}
              onInputChange={(e, val) => setClienteSearch(val)}
              onChange={(e, val) => setClienteSeleccionado(val)}
              renderInput={(params) => <TextField {...params} label="Cliente (Dueño) *" required />}
            />
          </Grid>

          {/* SELECT MASCOTA */}
          <Grid item xs={12}>
            <FormControl fullWidth disabled={!clienteSeleccionado || loadingPacientes}>
              <InputLabel>Mascota / Paciente *</InputLabel>
              <Select
                value={pacienteSeleccionado?.id_paciente || ''}
                label="Mascota / Paciente *"
                onChange={(e) => setPacienteSeleccionado(pacientesCliente.find(p => p.id_paciente === e.target.value))}
              >
                {pacientesCliente.map(p => (
                  <MenuItem key={p.id_paciente} value={p.id_paciente}>
                    {p.nombre_paciente} — {p.especie_nombre} / {p.raza_nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* AUTOCOMPLETE VETERINARIO (Búsqueda autocomplementaria) */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={veterinarios}
              loading={loadingVeterinarios}
              getOptionLabel={(option) => option.nombre_usuario || ''}
              value={veterinarioSeleccionado}
              onChange={(e, newValue) => setVeterinarioSeleccionado(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Veterinario Responsable" 
                  placeholder="Busca por nombre..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingVeterinarios ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* OTROS CAMPOS */}
          <Grid item xs={12} md={6}>
             <TextField
              label="Jaula / Box (opcional)"
              fullWidth
              value={idJaula}
              onChange={(e) => setIdJaula(e.target.value.replace(/\D/g, ''))}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Motivo de Ingreso *"
              multiline rows={3}
              fullWidth
              value={motivoIngreso}
              onChange={(e) => setMotivoIngreso(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Diagnóstico Presuntivo (opcional)"
              multiline rows={2}
              fullWidth
              value={diagnosticoPresuntivo}
              onChange={(e) => setDiagnosticoPresuntivo(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined" startIcon={<Close />}>Cancelar</Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          disabled={loadingGuardar || !clienteSeleccionado || !pacienteSeleccionado || !motivoIngreso.trim()}
          startIcon={loadingGuardar ? <CircularProgress size={20} color="inherit" /> : <Save />}
        >
          Registrar Ingreso
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NuevoIngresoModal;